import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API_URL, errMsg } from './shared';

const REASONS = [{ id: 'urlop', label: 'Urlop' }, { id: 'choroba', label: 'Choroba' }, { id: 'wolne', label: 'Wolne' }];

const plDate = (iso) => iso.split('-').reverse().join('.');

const MissingRow = ({ w, date, canManage, onAdd, onChanged }) => {
  const [reason, setReason] = useState('');
  const markAbsent = async () => {
    try {
      await axios.post(`${API_URL}/godziny/absences`, { worker_id: w.id, date, reason });
      toast.success(`${w.name}: oznaczono „Nie pracował”`);
      onChanged();
    } catch (e) { toast.error(errMsg(e, 'Błąd zapisu')); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-amber-500/20 last:border-0" data-testid={`missing-worker-${w.id}`}>
      <span className="font-medium text-[var(--crm-text-primary)] min-w-[160px]">{w.name}</span>
      {canManage && (
        <>
          <Button size="sm" onClick={() => onAdd(w)} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid={`missing-add-${w.id}`}>
            <Plus className="w-4 h-4 mr-1" />Dodaj wpis ręcznie
          </Button>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="w-36 h-9" data-testid={`missing-reason-${w.id}`}><SelectValue placeholder="Powód" /></SelectTrigger>
            <SelectContent>{REASONS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" disabled={!reason} onClick={markAbsent} data-testid={`missing-absent-${w.id}`}>Nie pracował</Button>
        </>
      )}
    </div>
  );
};

export const MissingHoursBox = ({ canManage, onAdd, refreshKey }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API_URL}/godziny/missing`).then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d || (!d.workers.length && !d.absences.length)) return null;
  const undo = async (a) => {
    try { await axios.delete(`${API_URL}/godziny/absences/${a.id}`); load(); } catch (e) { toast.error(errMsg(e, 'Błąd')); }
  };
  return (
    <Card className="bg-amber-500/10 border-amber-500/40 p-4 mb-2" data-testid="missing-hours-box">
      {d.workers.length > 0 && (
        <>
          <p className="font-semibold text-amber-600 dark:text-amber-300 flex items-center gap-2 mb-2" data-testid="missing-hours-title">
            <AlertTriangle className="w-4 h-4" />Brak wpisów z {plDate(d.date)}
          </p>
          {d.workers.map((w) => <MissingRow key={w.id} w={w} date={d.date} canManage={canManage} onAdd={(x) => onAdd(x, d.date)} onChanged={load} />)}
        </>
      )}
      {d.absences.length > 0 && (
        <p className="text-xs text-[var(--crm-text-secondary)] mt-2" data-testid="missing-absences">
          Nie pracowali {plDate(d.date)}:{' '}
          {d.absences.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 mr-3">
              {a.worker_name} ({a.reason_label})
              {canManage && (
                <button type="button" onClick={() => undo(a)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-amber-500/20 text-[var(--crm-text-primary)]"
                  data-testid={`absence-undo-${a.id}`}><Undo2 className="w-4 h-4" />cofnij</button>
              )}
            </span>
          ))}
        </p>
      )}
    </Card>
  );
};
